<template>
  <div class="page">
    <app-title
      v-if="!organizationLoading"
      :page="organization.name"
      :notice="notice"
      :notice-title="noticeTitle"
      :notice-type="noticeType"
      :notice-link="noticeLink"
      :notice-link-text="noticeLinkText"
      @closed="sawNotice">
      <p
        slot="content"
        class="font-size-4 mb2">Play with your numbers.</p>
    </app-title>
    <section class="grid grid-columns-2_sm grid-columns-3_md vh33 mb-margin">
      <div class="px2 py1 subtle flex flex-column">
        <h2 class="mb1"><router-link
          :to="{ name: 'estimates' }"
          active-class="active">Estimates</router-link></h2>
        <lw-table
          v-if="estimates.length"
          :type="'lined'"
        >
          <tr
            v-for="estimate in estimates"
            v-if="!estimatesLoading"
            :key="estimate.id">
            <td class="title"><router-link
              :to="{ name: 'estimate', params: { id: estimate.id }}"
              active-class="active">{{ estimate.project }}</router-link></td>
            <td>{{ estimate.client }}</td>
          </tr>
        </lw-table>
        <div
          v-else
          class="flex flex-column flex-grow">
          <div class="flex-grow">
            <hr class="border-width-3 mt0">
            <p>Make an estimate for an upcoming project.</p>
          </div>
          <button
            class="accent-button large-button justify-self-flex-end mt2"
            @click="$router.push({ name: 'newEstimate' })">Make your first estimate</button>
        </div>
      </div>
      <div class="px2 py1 subtle flex flex-column">
        <h2 class="mb1"><router-link
          :to="{ name: 'redflags' }"
          active-class="active">Red flags</router-link></h2>
        <lw-table
          v-if="redFlags.length"
          :type="'lined'"
        >
          <tr
            v-for="redFlag in redFlags"
            v-if="!redFlagLoading"
            :key="redFlag.id">
            <td class="title"><router-link
              :to="{ name: 'redflag', params: { id: redFlag.id }}"
              active-class="active">{{ redFlag.title }}</router-link></td>
          </tr>
        </lw-table>
        <div
          v-else
          class="flex flex-column flex-grow">
          <div class="flex-grow">
            <hr class="border-width-3 mt0">
            <p>Red flags help indicate risks on a project. Create your own library of red flags, rate their impact on your project roles, and apply them to estimates for safer, more accurate estimates.</p>
          </div>
          <button
            class="accent-button large-button mt2"
            @click="$router.push({ name: 'newRedFlag' })">Log your first red flag</button>
        </div>
      </div>
      <div class="px2 py1 subtle flex flex-column">
        <h2 class="mb1"><router-link
          :to="{ name: 'rates' }"
          active-class="active">Rate cards</router-link></h2>
        <lw-table
          v-if="rates.length"
          :type="'lined'"
        >
          <tr
            v-for="rate in rates"
            v-if="!ratesLoading"
            :key="rate.id">
            <td class="title">
              <div v-if="rate.mloe || rate.type === 'Calculated'"><router-link
                :class="'mr1'"
                :to="{ name: 'rate', params: { id: rate.id }}">{{ rate.name }}</router-link> <span class="font-size-1 grey-600-text medium">{{ rate.type }}</span></div>
              <div v-else><router-link
                :class="'mr1'"
                :to="{ name: 'simpleRate', params: { id: rate.id }}">{{ rate.name }}</router-link> <span class="font-size-1 grey-600-text medium">{{ rate.type }}</span></div>
            </td>
          </tr>
        </lw-table>
        <div
          v-else
          class="flex flex-column flex-grow">
          <div class="flex-grow">
            <hr class="border-width-3 mt0">
            <p>What do you charge your clients? How much <em>should</em> you charge? Calculate a robust rate framework based on your organizational goals and get closer to sustainability. Already have a set of tried and true rates and want to get estimating? Add a manual card.</p>
          </div>
          <div class="flex_lg mt2">
            <button
              class="c1 large-button accent-button mr1"
              @click="$router.push({ name: 'newRateGeneral' })">Calculate your first rate card</button>
            <button
              class="c1 large-button accent-button"
              @click="$router.push({ name: 'newRateSimple' })">Add your first manual rate card</button>
          </div>
        </div>
      </div>

    </section>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import Table from './livewires/Table'
import page from './mixins/page'
import sync from './mixins/sync'

export default {
  components: {
    lwTable: Table
  },
  mixins: [
    page,
    sync
  ],
  data () {
    return {
      noticeId: null,
      notice: null,
      noticeTitle: null,
      noticeType: null,
      noticeLink: false,
      noticeLinkText: null,
      change: false
    }
  },
  computed: {
    ...mapState([
      'changelog'
    ]),
    ...mapGetters([
      'organization',
      'user',
      'rates',
      'estimates',
      'redFlags',
      'team',
      'roles',
      'userLoading',
      'estimatesLoading',
      'ratesLoading',
      'redFlagLoading',
      'latestChange'
    ])
  },
  watch: {
    user (latest, former) {
      if (!latest.changelog) {
        latest.changelog = {
          v0: null
        }
      }
      this.checkChanges(latest.changelog)
    }
  },
  methods: {
    sawNotice () {
      const payload = {}
      payload.user = { ...this.user }
      payload.notice = this.noticeId
      this.$store.dispatch('sawChangelog', payload)
    },
    checkChanges (latest) {
      if (!(this.latestChange.version in latest)) {
        this.noticeId = this.latestChange.version
        this.notice = this.latestChange.summary
        this.noticeLink = this.latestChange.notes
        this.noticeLinkText = 'Read the full v' + this.latestChange.version + ' changelog'
        this.noticeTitle = this.latestChange.title
      } else {
        this.noticeId = null
        this.notice = null
        this.noticeLink = false
        this.noticeLinkText = null
        this.noticeTitle = null
      }
    }
  }
}
</script>
